import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input } from 'antd';
import { validateEmail } from '../../Validators/ValidatorEmail';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface EmailListFormComponentProps {
  propertyReference: string;
  parentReference: Array<string | number>;
  buttonTitle: string;
  emailType: string;
  // when set, at least one email is required and this is the message
  requiredMessage?: string;
}

export const EmailListFormComponent = ({
  propertyReference,
  parentReference,
  buttonTitle,
  emailType,
  requiredMessage,
}: EmailListFormComponentProps): ReactNode => {
  const { t } = useTranslation();
  return (
    <Form.List
      name={[...parentReference, propertyReference]}
      rules={[
        {
          // The list is checked from the form values, so emails filled in from a saved setup are checked too
          validator: async (_rule, emails?: Array<string>): Promise<void> => {
            const filledEmails = (emails ?? [])
              .filter(Boolean)
              .map((email) => email.trim().toLowerCase());
            if (requiredMessage && filledEmails.length === 0) {
              throw new Error(requiredMessage);
            }
            if (new Set(filledEmails).size !== filledEmails.length) {
              throw new Error(t('validator.duplicateEmail'));
            }
          },
        },
      ]}
    >
      {(fields, fieldOperations, { errors }) => (
        <>
          {fields.map((field) => (
            <Flex key={field.key} gap={8} align="start">
              <Form.Item
                style={{ flex: 1 }}
                name={[field.name]}
                rules={[{ required: true }, { validator: validateEmail }]}
              >
                <Input placeholder={`${emailType} Email`} />
              </Form.Item>
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                aria-label={t('uniForm.removeEmail')}
                title={t('uniForm.removeEmail')}
                onClick={() => fieldOperations.remove(field.name)}
              />
            </Flex>
          ))}
          <Form.Item style={{ marginBottom: errors.length > 0 ? 24 : 0 }}>
            <Form.ErrorList errors={errors} />
          </Form.Item>
          <Form.Item
            style={{
              justifySelf: 'center',
            }}
          >
            <Button type="primary" onClick={() => fieldOperations.add()}>
              {buttonTitle}
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
};
